/**
 * Obvious "an update is ready" notice for the Home dashboard and the locked session.
 * Dismissing it does not hide the sidebar row.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert } from './Alert';
import { Button } from './Button';
import { useUpdateContext } from '../hooks/useUpdateContext';

export interface UpdateAvailableBannerProps {
  /** `compact` drops the message and labels the action "Update Available". */
  variant?: 'full' | 'compact';
  /** Hides the banner while keeping it mounted, so a "Later" dismissal survives. */
  hidden?: boolean;
}

export function UpdateAvailableBanner({ variant = 'full', hidden = false }: UpdateAvailableBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, newVersion, applyUpdate } = useUpdateContext();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

  const versionKey = `${status}:${newVersion ?? ''}`;
  useEffect(() => {
    setDismissedVersion(null);
  }, [versionKey]);

  if (hidden) return null;
  if (status !== 'available' && status !== 'ready') return null;
  if (dismissedVersion === versionKey) return null;

  const isCompact = variant === 'compact';

  let message: string | null = null;
  if (!isCompact) {
    message = status === 'ready'
      ? t('identity.e2e.updateBanner.ready')
      : newVersion
        ? t('identity.e2e.updateBanner.messageVersion', { version: newVersion })
        : t('identity.e2e.updateBanner.message');
  }

  const primaryLabel = status === 'ready'
    ? t('identity.e2e.updateBanner.restart')
    : isCompact
      ? t('identity.e2e.updateBanner.available')
      : t('identity.e2e.updateBanner.viewDetails');

  return (
    <div
      className={`key-storage-banner update-available-banner${isCompact ? ' update-available-banner--compact' : ''}`}
    >
      <Alert variant="info">
        <div className="update-available-banner-content">
          {message && <span className="update-available-banner-message">{message}</span>}
          <div className="update-available-banner-actions">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (status === 'ready') {
                  applyUpdate();
                  return;
                }
                navigate('/about/updates');
              }}
            >
              {primaryLabel}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDismissedVersion(versionKey)}
            >
              {t('identity.e2e.updateBanner.later')}
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
}
